import Router from 'next/router'

import { notification } from 'antd'
import axios, { AxiosError, AxiosInstance } from 'axios'
import { BASE_API_URL } from 'constant'

import parseJWT from 'helpers/parseJWT'
import CODE from 'utils/constants/code.constant'
import PUBLIC_PAGES from 'utils/constants/page.constant'

import requestInterceptorList from './Interceptors/Request'

export const extractStringMessage = (raw: unknown): string | undefined => {
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number') return String(raw)
  if (raw && typeof raw === 'object') {
    const obj = raw as { message?: unknown; error?: unknown }
    return (
      extractStringMessage(obj.message) ||
      extractStringMessage(obj.error) ||
      JSON.stringify(raw)
    )
  }
  return undefined
}

export const getErrorMessage = async (
  error: any,
): Promise<string | undefined> => {
  const errorMessage =
    error?.config?.responseType === 'blob'
      ? JSON.parse(await error?.response?.data?.text())
      : {}
  const raw =
    errorMessage?.error?.message ||
    error?.response?.data?.errors ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message
  return extractStringMessage(raw)
}

notification.config({
  maxCount: 1,
})
function showNotification(error: AxiosError) {
  const errorStatus = error?.response?.status || 400
  const errorCode = error?.response?.data?.code || '00'

  if (errorStatus === 503 && errorCode === CODE.MAINTENANCE) {
    const referer = Router.query.referer as string
    Router.push(`/maintenance?referer=${referer ? referer : Router.pathname}`)
    return
  }

  return (
    error?.response?.data?.errors ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message
  )
}

function resetAuthTokenRequest() {
  authTokenRequest = null
}

let authTokenRequest: Promise<any> | null
const getAuthToken = (formData, keyPin) => {
  if (!authTokenRequest) {
    authTokenRequest = axios.post(`${BASE_API_URL}/refresh_token/`, formData, {
      headers: {
        kid: keyPin,
      },
    })
    authTokenRequest.then(resetAuthTokenRequest, resetAuthTokenRequest)
  }

  return authTokenRequest
}

/**
 * トークン更新中の待機キュー
 *
 * 画面を開くと複数のAPIが同時に走るため、全部が同時に401を受けると
 * トークン更新もリクエストの本数だけ飛んでしまう。
 * 更新は1本だけ走らせ、残りはここに並んで完了を待ち、
 * 新しいトークンで再送する。
 */
type PendingRequest = {
  resolve: (accessToken: string) => void
  reject: (error: any) => void
}

let isRefreshingToken = false
let pendingRequests: PendingRequest[] = []

/**
 * 更新の結果を待機中の全リクエストへ配る。
 * 失敗したときも必ず reject する。誰にも配らないと待機側の Promise が
 * 永久に解決せず、画面が読み込み中のまま止まるため。
 */
function flushPendingRequests(error: any, accessToken?: string) {
  const queued = pendingRequests
  // 配る前に空にする。再送がその場で新たな401を起こしても
  // 同じ待機者へ二重に配らないようにするため。
  pendingRequests = []

  queued.forEach((pending) => {
    if (error) {
      pending.reject(error)
      return
    }
    pending.resolve(accessToken as string)
  })
}

function createAuthAxios(
  baseURL: string,
  keyLocalStorage?: string,
  keyPin?: string,
): AxiosInstance {
  const instanceAxios = axios.create({
    baseURL: process.env.NODE_ENV === 'test' ? undefined : baseURL,
    withCredentials: true,
    timeout: 600000, // 10 minutes
  })

  if (keyLocalStorage && keyPin) {
    instanceAxios.interceptors.request.use((config) => {
      const curConfig = { ...config }

      // if (config.url?.includes('/search_read')) {
      //   if (!config.data?.params?.fields?.length) {
      //     console.warn('has no fields [PROPERTY]', config.url)
      //   }
      // }

      // if (config.url?.includes('/read')) {
      //   if (!config.data?.params?.args?.[1]?.length) {
      //     console.warn('has no fields [ARGS]', config.url)
      //   }
      //   if (!config.data?.params?.fields?.length) {
      //     console.warn('has no fields [PROPERTY]', config.url)
      //   }
      // }

      // ALWAYS READ UPDATED TOKEN
      try {
        curConfig.headers.Authorization = `Bearer ${localStorage.getItem(
          keyLocalStorage,
        )}`
        curConfig.headers.kid = localStorage.getItem(keyPin)
        // curConfig.headers['public-key-pins'] = localStorage.getItem(keyPin)
      } catch (e) {
        // console.log('AXIOS CATCH: ', e)
      }
      return curConfig
    })

    instanceAxios.interceptors.response.use(
      async (response) => {
        // const errorName = 'odoo.exceptions.AccessError'
        try {
          const refreshToken = localStorage.getItem('refresh-token')
          const parsedJwt = parseJWT(refreshToken)
          const keyPin = localStorage.getItem('key-pin')
          if (
            (!keyPin || !parsedJwt) &&
            !PUBLIC_PAGES.includes(Router.pathname)
          ) {
            window.location.replace(
              `/?prevPage=${window.location.pathname}${window.location.search}&error=token_deleted`,
            )
          }
        } catch (err) {
          //
        }

        // if (
        //   refreshToken &&
        //   keyPin &&
        //   response.data.error &&
        //   response.data.error.data.name === errorName &&
        //   !response.data.error.data.message.includes(
        //     'Due to security restrictions',
        //   ) &&
        //   !response.data.error.data.message.includes(
        //     'cannot be called remotely.',
        //   )
        // ) {
        //   const originalRequest = response.config as any
        //   originalRequest._retry = true
        //   const formData = new FormData()
        //   formData.append('refresh_token', refreshToken)
        //   try {
        //     const res = await axios.post(
        //       `${BASE_API_URL}/refresh_token`,
        //       formData,
        //       {
        //         headers: {
        //           kid: keyPin,
        //           // 'public-key-pins': keyPin,
        //         },
        //       },
        //     )
        //     if (res?.data?.error) {
        //       notification.error({
        //         message: res.data.error?.data?.message,
        //       })
        //       localStorage.removeItem('token-ldx')
        //       localStorage.removeItem('refresh-token')
        //       Router.reload()
        //     } else {
        //       const accessToken = res.data?.access_token
        //       originalRequest.headers.Authorization = `Bearer ${accessToken}`
        //       localStorage.setItem('token-ldx', res.data?.access_token)
        //       localStorage.setItem('refresh-token', res.data?.refresh_token)
        //       return instanceAxios(originalRequest)
        //     }
        //   } catch (err) {
        //     notification.error({
        //       message: 'Failed refresh token :(',
        //     })
        //     localStorage.removeItem('token-ldx')
        //     localStorage.removeItem('refresh-token')
        //     Router.reload()
        //   }
        // }
        return response
      },
      async (error) => {
        const isUrlAclFull = error.response?.config?.url?.includes('/acl/full')
        if (error?.message?.includes('timeout')) {
          return Promise.reject(error)
        }

        if (error.response?.status === 400) {
          return Promise.reject({
            code: error?.response?.data?.code || 1,
            message: await getErrorMessage(error),
          })
        }

        if (error.response?.status === 500 && !isUrlAclFull) {
          notification.error({
            message: await getErrorMessage(error),
          })
          return Promise.reject({
            code: 500,
            message: await getErrorMessage(error),
          })
        }

        const originalRequest = error.config
        const refreshToken = localStorage.getItem('refresh-token')
        const keyPin = localStorage.getItem('key-pin')
        const formData = new FormData()
        formData.append('refresh_token', refreshToken)

        if (
          (error.response?.status === 401 || error.response?.status === 403) &&
          !!refreshToken &&
          // 1リクエストにつきトークン更新は1回まで。
          // _retry は元から書かれていたが条件から漏れていて読まれておらず、
          // 権限不足の401では「更新 → 再送 → また401 → 更新」が
          // 終わらなかった。トークンを新しくしても権限は増えないので、
          // 再送後の2回目の401は呼び出し側へ返して判断させる。
          !originalRequest?._retry
        ) {
          originalRequest._retry = true

          // 既に別のリクエストが更新中なら、更新は増やさず完了を待つ
          if (isRefreshingToken) {
            return new Promise((resolve, reject) => {
              pendingRequests.push({
                resolve: (accessToken: string) => {
                  originalRequest.headers.Authorization = `Bearer ${accessToken}`
                  resolve(instanceAxios(originalRequest))
                },
                reject,
              })
            })
          }

          isRefreshingToken = true
          return (
            getAuthToken(formData, keyPin)
              .then((res) => {
                const accessToken = res.data?.access_token
                originalRequest.headers.Authorization = `Bearer ${accessToken}`
                localStorage.setItem('token-ldx', res.data?.access_token)
                localStorage.setItem('refresh-token', res.data?.refresh_token)

                isRefreshingToken = false
                // 待機中のリクエストを新しいトークンで送り出す。
                flushPendingRequests(null, accessToken)
                // ⛔自分の再送は**ここでしない** —— 下の .then で行う。
                //   以前はここで instanceAxios(originalRequest) を返して
                //   いたため、**再送の失敗まで下の .catch が拾って**いた
                //   (2026-09-09・PR #18698 レビュー [P1])。更新Aが成功→
                //   遅れて来た旧トークンの401が更新Bを開始→再送Aが500で
                //   落ちる、の順で、**更新Bを待つリクエストがAの無関係な
                //   エラーで全部落ちた**。条件次第でトークンを消して
                //   ログイン画面へ飛ばすことまで起きる。
              })
              .catch((error) => {
                isRefreshingToken = false
                // 更新に失敗したら待機中の全員に同じエラーを配る
                flushPendingRequests(error)

                if (
                  error?.response?.data?.errors ===
                    'Refresh token is invalid or expired' ||
                  error?.response?.data?.error?.message ===
                    'Token or refresh token is invalid or expired'
                ) {
                  localStorage.removeItem('token-ldx')
                  localStorage.removeItem('refresh-token')
                  localStorage.removeItem('key-pin')
                  localStorage.removeItem('filters')

                  window.location.replace(
                    `/?prevPage=${window.location.pathname}${window.location.search}&error=token_expired`,
                  )
                }

                // 以前はここで何も返しておらず、条件に合わない失敗は
                // Promise が undefined で「解決」していた。呼び出し側は
                // res.data で TypeError になるか、isError(undefined) が true を
                // 返して別の例外になる。必ず再rejectして呼び出し側へ届ける。
                return Promise.reject(error)
              })
              // ⭕自分の再送は**更新の成否を見終えてから**。更新が
              //   失敗したときは上の catch が再rejectするので、ここは
              //   実行されない。これで catch に来るのは**トークン更新の
              //   失敗だけ**になる。
              .then(() => instanceAxios(originalRequest))
          )
        }
        return Promise.reject(error)
      },
    )
  }

  instanceAxios.interceptors.response.use(
    (response) => {
      return response
    },
    (error: AxiosError) => {
      if (!error.message?.includes('403') && !axios.isCancel(error)) {
        showNotification(error)
      }
      return Promise.reject(error)
    },
  )

  // Flexible Request Interceptors
  requestInterceptorList.forEach((hook) =>
    instanceAxios.interceptors.request.use(hook),
  )

  return instanceAxios
}

const Fetcher = {
  createAuthAxios,
}

export default Fetcher
